import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, concatMap, retryWhen } from 'rxjs/operators';
import { AuthStateService } from '../../shared/services/auth-state.service';
import { ToastService } from '../service/toast.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private toastService: ToastService,
    private translate: TranslateService,
    private router: Router,
    private authState: AuthStateService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      retryWhen((errors) =>
        errors.pipe(
          concatMap((error: HttpErrorResponse, index: number) => {
            if (error.status === 429 && index < 2) {
              const retryMessage = this.translate.instant('ERROR_MESSAGES.RETRY_MESSAGE', { attempt: index + 2 });
              this.toastService.showWarning(retryMessage);
              return timer(2000);
            }
            return throwError(error);
          }),
        ),
      ),
      catchError((error: HttpErrorResponse) => {
        this.handleError(error);
        return throwError({ ...error, userMessage: this.getErrorMessage(error) });
      }),
    );
  }

  // Endpoints that run during bootstrap or SSO handshake — suppress user-facing
  // toasts for these so a transient network blip at startup doesn't alarm the user.
  private readonly silentUrls = ['/auth/brand-token', '/sso/authorization-url', '/sso/callback'];

  private isSilentUrl(url: string | null): boolean {
    return !!url && this.silentUrls.some((s) => url.includes(s));
  }

  private handleError(error: HttpErrorResponse): void {
    const message = this.getErrorMessage(error);

    switch (error.status) {
      case 429:
        this.toastService.showError(message);
        break;
      case 0:
        // Skip the toast for bootstrap/SSO URLs — these fail silently by design.
        if (!this.isSilentUrl(error.url)) {
          this.toastService.showError(this.translate.instant('ERROR_MESSAGES.CONNECTION_ERROR'));
        }
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        this.toastService.showError(message);
        break;
      case 401:
        // Skip redirect for login API calls — a wrong-password 401 should stay on the login page.
        // Also skip for SSO/brand-token endpoints that run before a session exists.
        if (!error.url?.includes('/login') && !this.isSilentUrl(error.url)) {
          this.authState.clear();
          this.toastService.showWarning(this.translate.instant('ERROR_MESSAGES.SESSION_EXPIRED'));
          this.router.navigateByUrl('/login');
        }
        break;
      case 403:
        this.toastService.showWarning(this.translate.instant('ERROR_MESSAGES.NO_PERMISSION'));
        break;
      case 404:
        this.toastService.showInfo(this.translate.instant('ERROR_MESSAGES.RESOURCE_NOT_FOUND'));
        break;
      default:
        if (error.status >= 400) {
          this.toastService.showError(message);
        }
    }
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    switch (error.status) {
      case 429:
        return this.translate.instant('ERROR_MESSAGES.RATE_LIMIT');
      case 0:
        return this.translate.instant('ERROR_MESSAGES.CONNECTION_ERROR');
      case 401:
        return this.translate.instant('ERROR_MESSAGES.AUTH_REQUIRED');
      case 403:
        return this.translate.instant('ERROR_MESSAGES.ACCESS_DENIED');
      case 404:
        return this.translate.instant('ERROR_MESSAGES.NOT_FOUND');
      case 500:
        return this.translate.instant('ERROR_MESSAGES.SERVER_ERROR');
      case 502:
        return this.translate.instant('ERROR_MESSAGES.BAD_GATEWAY');
      case 503:
        return this.translate.instant('ERROR_MESSAGES.SERVICE_UNAVAILABLE');
      case 504:
        return this.translate.instant('ERROR_MESSAGES.GATEWAY_TIMEOUT');
      default:
        if (error.status >= 400) {
          return this.translate.instant('ERROR_MESSAGES.GENERIC_ERROR', { status: error.status });
        }
        return this.translate.instant('ERROR_MESSAGES.UNEXPECTED_ERROR');
    }
  }
}
